// src/pages/BecomeLegalAdvisor.js

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { countries } from '../data/locationData';
import '../styles/BecomeLegalAdvisor.css';
//rftghjk
const BecomeLegalAdvisor = () => {
  const [formData, setFormData] = useState({
    practiceCountry: '',
    enrollmentNumber: '',
    specialization: '',
    experience: '',
    languagesKnown: '',
    primaryPracticeAreas: '',
    address: '',
    about: '', // New: about
  });
  const [certificate, setCertificate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [showPopup, setShowPopup] = useState(false);
  const [popupMessage, setPopupMessage] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  const [isAlreadyLegalAdvisor, setIsAlreadyLegalAdvisor] = useState(false);
  const [reapplyMode, setReapplyMode] = useState(false);
  const [statusInfo, setStatusInfo] = useState(null);
  // Holds the outcome after submitting so we can show it as the next popup
  // instead of a native browser alert(). Shape: { status, message }.
  const [submitResult, setSubmitResult] = useState(null);
  const navigate = useNavigate();

  // Check if user is already a legal advisor
  useEffect(() => {
    const checkUserStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setUserLoading(false);
        return;
      }

      try {
        const response = await axios.get('/api/auth/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const user = response.data.user;
        setCurrentUser(user);
        // Prefill country of practice from the user's profile country.
        if (user.country) {
          setFormData(prev => ({ ...prev, practiceCountry: user.country }));
        }

        // Check if user is already a legal advisor / has an application in flight.
        if (user.role === 'LegalAdvisor' || user.enrollmentNumber) {
          // Pull the latest verification status to decide what to show.
          let status = null;
          try {
            const statusRes = await axios.get('/api/advisor-verification/status', {
              headers: { Authorization: `Bearer ${token}` },
            });
            setStatusInfo(statusRes.data);
            status = statusRes.data?.verificationStatus;
          } catch (e) {
            // Non-fatal; fall back to a generic message.
          }

          if (status === 'rejected') {
            // Rejected applicants may fix the issue and re-apply — prefill the form.
            setReapplyMode(true);
            setFormData(prev => ({
              ...prev,
              practiceCountry: user.verificationCountry || user.country || prev.practiceCountry || '',
              enrollmentNumber: user.enrollmentNumber || '',
              specialization: user.specialization || '',
              experience: user.experience != null ? String(user.experience) : '',
              languagesKnown: Array.isArray(user.languagesKnown) ? user.languagesKnown.join(', ') : (user.languagesKnown || ''),
              primaryPracticeAreas: Array.isArray(user.primaryPracticeAreas) ? user.primaryPracticeAreas.join(', ') : (user.primaryPracticeAreas || ''),
              address: user.address || '',
              about: user.about || '',
            }));
          } else {
            // Verified or pending — block re-submission and show status.
            setIsAlreadyLegalAdvisor(true);
            setPopupMessage(
              status === 'pending'
                ? 'Your application is submitted and pending review by our team.'
                : 'You are already a legal advisor of our app!'
            );
            setShowPopup(true);
          }
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      } finally {
        setUserLoading(false);
      }
    };

    checkUserStatus();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    setCertificate(e.target.files[0] || null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      setPopupMessage('You need to login first');
      setShowPopup(true);
      return;
    }
    if (!formData.practiceCountry) {
      setPopupMessage('Please select your country of practice to continue.');
      setShowPopup(true);
      return;
    }
    if (!certificate) {
      setPopupMessage('Please upload your legal-practice certificate / licence / ID to continue.');
      setShowPopup(true);
      return;
    }

    setPopupMessage('Submit your details for verification? Your uploaded credential will be checked automatically.');
    setShowPopup(true);
  };

  const handlePopupOk = async () => {
    try {
      setSubmitting(true);
      const token = localStorage.getItem('token');

      const payload = new FormData();
      payload.append('country', formData.practiceCountry);
      payload.append('enrollmentNumber', formData.enrollmentNumber);
      payload.append('specialization', formData.specialization);
      payload.append('experience', formData.experience);
      payload.append('languagesKnown', formData.languagesKnown);
      payload.append('primaryPracticeAreas', formData.primaryPracticeAreas);
      payload.append('address', formData.address);
      payload.append('about', formData.about);
      payload.append('certificate', certificate);

      const res = await axios.post(
        '/api/advisor-verification/submit',
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        }
      );

      const { status, msg } = res.data;
      // Show the outcome as the next in-app popup (no native alert()).
      if (status === 'verified') {
        setSubmitResult({
          status: 'verified',
          message: msg || 'Your credentials were verified automatically. You are now a verified legal advisor.',
        });
      } else {
        setSubmitResult({
          status: 'pending',
          message: msg || 'Your application was submitted and is pending review by our team.',
        });
      }
    } catch (err) {
      setSubmitResult({
        status: 'error',
        message: err.response?.data?.msg || 'Failed to submit for verification',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Dismiss the result popup and route the user based on the outcome.
  const handleResultOk = () => {
    const status = submitResult?.status;
    setSubmitResult(null);
    setShowPopup(false);
    if (status === 'verified') {
      navigate('/legal-advisors');
    } else if (status === 'pending') {
      navigate('/profile');
    }
    // 'error' → just close so the user can fix and retry.
  };

  const handlePopupCancel = () => {
    if (isAlreadyLegalAdvisor) {
      navigate('/legal-advisors'); // Navigate to legal advisors page if already a legal advisor
    } else {
      setShowPopup(false);
    }
  };

  if (userLoading) {
    return (
      <div className="bla-page">
        <div className="become-legal-advisor-container bla-loading">
          <div className="bla-spinner" />
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  // Friendly description of the current verification status.
  const statusText = (() => {
    if (!statusInfo) return null;
    switch (statusInfo.verificationStatus) {
      case 'verified':
        return '✅ Your account is verified.';
      case 'pending':
        return '⏳ Your verification is pending review by our team.';
      case 'rejected':
        return `❌ Your verification was rejected.${statusInfo.verificationNotes ? ' Reason: ' + statusInfo.verificationNotes : ''}`;
      default:
        return null;
    }
  })();

  // If user is already a legal advisor, show only the popup
  if (isAlreadyLegalAdvisor) {
    return (
      <div className="bla-page">
        {showPopup && (
          <div className="popup-overlay">
            <div className="popup-content">
              <h3>✅ Already a Legal Advisor</h3>
              <p>{popupMessage}</p>
              {statusText && <p style={{ marginTop: 8, fontWeight: 600 }}>{statusText}</p>}
              <div className="popup-buttons">
                <button className="ok-button" onClick={handlePopupCancel}>
                  Go to Legal Advisors
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  const isIndiaPractice = (formData.practiceCountry || '').toLowerCase().includes('india');
  const enrollmentLabel = isIndiaPractice ? 'Bar Council Enrollment Number' : 'Bar / Licence Number';
  const docLabel = isIndiaPractice
    ? 'Bar Council Enrolment Certificate or Advocate ID Card'
    : 'Bar Admission / Practising Certificate or Lawyer Licence/ID';

  return (
    <div className="bla-page">
      <div className="become-legal-advisor-container">
        <aside className="bla-header">
          <div className="bla-header-icon">⚖️</div>
          <h2>Become a Legal Advisor</h2>
          <p className="bla-subtitle">
            Join our trusted network of verified legal professionals. Complete your profile and
            upload your credentials — most Indian applications are verified instantly.
          </p>
          <ul className="bla-points">
            <li>
              <span className="bla-point-icon">✓</span>
              <span>Fill in your practice details and licence / enrollment number</span>
            </li>
            <li>
              <span className="bla-point-icon">✓</span>
              <span>Upload your Bar Council certificate or advocate ID card</span>
            </li>
            <li>
              <span className="bla-point-icon">✓</span>
              <span>Get auto-verified (India) or a quick manual review (other countries)</span>
            </li>
          </ul>
          <div className="bla-badges">
            <span className="bla-badge">🛡️ Verified profiles</span>
            <span className="bla-badge">⚡ Instant auto-check</span>
            <span className="bla-badge">🔒 Secure &amp; private</span>
          </div>
        </aside>

        <form onSubmit={handleSubmit} className="legal-form">
          {reapplyMode && (
            <div className="bla-reject-banner">
              <div className="bla-reject-title">❌ Your previous application was not approved</div>
              {statusInfo?.verificationNotes && (
                <div className="bla-reject-reason">
                  <strong>Reason:</strong> {statusInfo.verificationNotes}
                </div>
              )}
              <div className="bla-reject-hint">
                Please fix the issue above (for example, upload a clearer document) and re-submit your application.
              </div>
            </div>
          )}

          <div className="bla-section-title">Practice details</div>

          <div className="bla-field">
            <label className="field-label" htmlFor="practiceCountry">
              Country of Practice <span className="req">*</span>
            </label>
            <select
              id="practiceCountry"
              name="practiceCountry"
              value={formData.practiceCountry}
              onChange={handleChange}
              required
            >
              <option value="">Select country where you practise law</option>
              {countries.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
            {!isIndiaPractice && formData.practiceCountry && (
              <small className="bla-hint bla-hint-warn">
                🌍 Applications outside India are confirmed by our team manually (usually 1–2 business days).
              </small>
            )}
          </div>

          <div className="bla-field">
            <label className="field-label" htmlFor="enrollmentNumber">
              {enrollmentLabel} <span className="req">*</span>
            </label>
            <input
              id="enrollmentNumber"
              type="text"
              name="enrollmentNumber"
              placeholder={isIndiaPractice ? 'e.g. KAR/1234/2019' : 'Your bar / licence number'}
              value={formData.enrollmentNumber}
              onChange={handleChange}
              required
            />
          </div>

          <div className="bla-grid">
            <div className="bla-field">
              <label className="field-label" htmlFor="specialization">
                Practice Areas &amp; Skills <span className="req">*</span>
              </label>
              <input
                id="specialization"
                type="text"
                name="specialization"
                placeholder="e.g. Criminal, Family, Corporate"
                value={formData.specialization}
                onChange={handleChange}
                required
              />
            </div>
            <div className="bla-field">
              <label className="field-label" htmlFor="experience">
                Experience (years) <span className="req">*</span>
              </label>
              <input
                id="experience"
                type="number"
                name="experience"
                placeholder="e.g. 5"
                value={formData.experience}
                onChange={handleChange}
                required
                min="0"
              />
            </div>
          </div>

          <div className="bla-grid">
            <div className="bla-field">
              <label className="field-label" htmlFor="languagesKnown">Languages Known</label>
              <input
                id="languagesKnown"
                type="text"
                name="languagesKnown"
                placeholder="Comma separated, e.g. English, Hindi"
                value={formData.languagesKnown}
                onChange={handleChange}
              />
            </div>
            <div className="bla-field">
              <label className="field-label" htmlFor="primaryPracticeAreas">Primary Practice Area(s)</label>
              <input
                id="primaryPracticeAreas"
                type="text"
                name="primaryPracticeAreas"
                placeholder="e.g. District Court, High Court"
                value={formData.primaryPracticeAreas}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="bla-field">
            <label className="field-label" htmlFor="address">Address</label>
            <input
              id="address"
              type="text"
              name="address"
              placeholder="Office / chamber address"
              value={formData.address}
              onChange={handleChange}
            />
          </div>

          <div className="bla-field">
            <label className="field-label" htmlFor="about">About You</label>
            <textarea
              id="about"
              name="about"
              placeholder="Write something about your legal practice, experience, notable areas, etc."
              value={formData.about}
              onChange={handleChange}
              rows={3}
            />
          </div>

          <div className="bla-section-title">Verification document</div>

          <div className="bla-field">
            <label className="field-label">
              Upload {docLabel} <span className="req">*</span>
            </label>
            <label className={`bla-upload ${certificate ? 'has-file' : ''}`}>
              <input
                type="file"
                name="certificate"
                accept=".pdf,.jpg,.jpeg,.png,.gif,.bmp,.webp,.tiff,.doc,.docx"
                onChange={handleFileChange}
                className="bla-upload-input"
              />
              <span className="bla-upload-icon">{certificate ? '✅' : '📄'}</span>
              <span className="bla-upload-text">
                {certificate ? certificate.name : 'Click to choose a file'}
              </span>
              <span className="bla-upload-hint">PDF, JPG, PNG, or Word · Max 15MB</span>
            </label>
            <small className="bla-hint">
              {isIndiaPractice
                ? 'Accepted: enrolment / practice certificate, or official State Bar Council advocate ID card. Checked automatically; unclear cases go to manual review.'
                : 'Accepted: any official proof you are licensed to practise law in your country (bar admission / practising certificate, attorney licence, or bar association ID). Reviewed by our team.'}
            </small>
          </div>

          <div className="become-legal-btn-row">
            <button type="submit" className="bla-btn-primary" disabled={submitting}>
              {submitting ? 'Submitting…' : reapplyMode ? 'Re-submit Application' : 'Submit for Verification'}
            </button>
            <button type="button" className="bla-btn-ghost" onClick={() => navigate('/legal-advisors')}>
              Cancel
            </button>
          </div>
        </form>
      </div>

      {/* Custom Popup */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            {submitResult ? (
              /* Post-submit outcome shown as the next popup (replaces alert) */
              <>
                <h3>
                  {submitResult.status === 'verified'
                    ? '🎉 You’re Verified!'
                    : submitResult.status === 'pending'
                    ? '⏳ Application Submitted'
                    : '⚠️ Submission Failed'}
                </h3>
                <p>{submitResult.message}</p>
                <div className="popup-buttons">
                  <button onClick={handleResultOk} className="popup-button ok-button">
                    {submitResult.status === 'verified'
                      ? 'Go to Legal Advisors'
                      : submitResult.status === 'pending'
                      ? 'Go to Profile'
                      : 'Close'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3>{isAlreadyLegalAdvisor ? '✅ Already a Legal Advisor' : 'Confirmation'}</h3>
                <p>{popupMessage}</p>
                <div className="popup-buttons">
                  {isAlreadyLegalAdvisor ? (
                    <button onClick={handlePopupCancel} className="popup-button ok-button">
                      Go to Legal Advisors
                    </button>
                  ) : (
                    <>
                      <button onClick={handlePopupOk} className="popup-button ok-button" disabled={submitting}>
                        {submitting ? 'Submitting...' : 'OK'}
                      </button>
                      <button onClick={handlePopupCancel} className="popup-button cancel-button" disabled={submitting}>
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BecomeLegalAdvisor;
